import {
  entityTypes,
  posthogEvents,
  REFERENCE_CURRENCY,
  shopifyPixelEvents
} from '@/shared/constants/constants';
import { useProductStore } from '@/shared/pinia/modules/product';
import { notifyBugsnagWithContext } from '@/shared/utils/error';
import { getProductDataFromHTML } from '@/shared/utils/product';

type KitProductData = {
  currencyCode: string | undefined;
  currencyRate: number | undefined;
  price: number | undefined;
  resolvedProductId: string | number | undefined;
  resolvedProductTitle: string | undefined;
  productVendor: string | undefined;
  entityId: string | undefined;
};

const buildPosthogData = ({
  currencyCode,
  currencyRate,
  price,
  resolvedProductId,
  resolvedProductTitle,
  productVendor,
  entityId
}: KitProductData): Record<string, unknown> => {
  const data: Record<string, unknown> = {
    url: window.location.href,
    currency_code: currencyCode,
    price,
    entity_id: entityId,
    product_id: resolvedProductId,
    product_type: entityTypes.KIT,
    product_vendor: productVendor,
    product_title: resolvedProductTitle,
    quantity: 1,
    total_amount: price
  };

  if (currencyRate) {
    data.currency_rate = currencyRate;
    if (price !== undefined) {
      data[`price_${REFERENCE_CURRENCY}`] = price * currencyRate;
      data[`total_amount_${REFERENCE_CURRENCY}`] = price * currencyRate;
    }
  }

  return data;
};

const buildShopifyData = ({
  currencyCode,
  price,
  resolvedProductId,
  resolvedProductTitle,
  productVendor
}: KitProductData) => ({
  cartLine: {
    merchandise: {
      price: { currencyCode, amount: price },
      product: {
        id: resolvedProductId,
        vendor: productVendor,
        title: resolvedProductTitle
      }
    },
    quantity: 1, //always 1 since Kits don't have a quantity selector
    cost: { totalAmount: { amount: price } }
  }
});

const publishKitAddedToCart = (data: Record<string, unknown>) => {
  try {
    window.Shopify.analytics.publish(
      shopifyPixelEvents.KIT_ADDED_TO_CART,
      data
    );
  } catch (error) {
    notifyBugsnagWithContext('trackKitAddedToCart', error, { data });
  }
};

// FlyBundles' cart transformation suppresses Shopify's native
// product_added_to_cart pixel event for kit products. Standard event names
// can't be republished via Shopify.analytics.publish, so we emit a
// namespaced custom event instead; the custom pixel re-emits it under the
// product_added_to_cart name for GTM.
export const trackKitAddedToCart = () => {
  const productStore = useProductStore();
  const { productPrice, productVendor, productName, productId } =
    getProductDataFromHTML();

  const kitData: KitProductData = {
    currencyCode: window.Shopify?.currency?.active,
    currencyRate: window.Hobbii?.hobbiiStoreData?.currencyRate,
    price: productPrice ? parseInt(productPrice, 10) / 100 : undefined,
    resolvedProductId:
      productStore.productId ??
      (productId ? parseInt(productId, 10) : undefined),
    resolvedProductTitle: productStore.productName || productName || undefined,
    productVendor: productVendor || undefined,
    entityId: productStore.resolvedEntityId
  };

  window.posthog?.capture(posthogEvents.ADD_TO_CART, buildPosthogData(kitData));
  publishKitAddedToCart(buildShopifyData(kitData));
};
